import React, { useEffect, useState } from 'react';
import { getHistoricalChampionships, getHistoricalSemifinals } from '../services/api';

const TROPHY = '🏆';

function GameRow({ winner, loser, winnerScore, loserScore, label }) {
    return (
        <div className="flex items-center gap-3 mb-2">
            <span className="text-gray-500 text-xs w-24 text-right shrink-0">{label}</span>
            <div className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 flex items-center justify-between">
                <span className="text-white font-bold text-sm">{winner}</span>
                <span className="text-white font-black">{winnerScore}</span>
            </div>
            <span className="text-gray-600 text-xs">vs</span>
            <div className="flex-1 bg-gray-900 border border-gray-800 rounded-lg px-3 py-2 flex items-center justify-between">
                <span className="text-gray-400 text-sm">{loser}</span>
                <span className="text-gray-400 font-black">{loserScore}</span>
            </div>
        </div>
    );
}

function History() {
    const [championships, setChampionships] = useState([]);
    const [semifinals,    setSemifinals]    = useState([]);
    const [gender,        setGender]        = useState('M');
    const [loading,       setLoading]       = useState(true);

    useEffect(() => {
        setLoading(true);
        Promise.all([
            getHistoricalChampionships(gender),
            getHistoricalSemifinals(null, gender)
        ])
        .then(([champRes, semiRes]) => {
            setChampionships(champRes.data);
            setSemifinals(semiRes.data);
        })
        .finally(() => setLoading(false));
    }, [gender]);

    const years = [...new Set(championships.map(c => c.year))].sort((a, b) => b - a);

    const getSemis = (year) => semifinals.filter(s => s.year === year);
    const getChamp = (year) => championships.find(c => c.year === year);

    return (
        <div className="max-w-3xl mx-auto px-4 py-8">
            <h2 className="text-2xl font-black text-white mb-2 uppercase tracking-wide">
                Championship History
            </h2>
            <p className="text-gray-500 text-sm mb-6">
                NCAA Division I Basketball — Final Four & Championship Results
            </p>

            <div className="flex gap-2 mb-8">
                <button
                    onClick={() => setGender('M')}
                    className={'px-4 py-2 rounded-lg text-sm font-bold transition-colors ' +
                        (gender === 'M' ? 'bg-[var(--org-accent,#f97316)] text-white' : 'bg-gray-800 text-gray-400 hover:text-white')}>
                    Men's
                </button>
                <button
                    onClick={() => setGender('W')}
                    className={'px-4 py-2 rounded-lg text-sm font-bold transition-colors ' +
                        (gender === 'W' ? 'bg-[var(--org-accent,#f97316)] text-white' : 'bg-gray-800 text-gray-400 hover:text-white')}>
                    Women's
                </button>
            </div>

            {loading ? (
                <div className="text-gray-400 text-center py-12">Loading...</div>
            ) : (
                <div className="space-y-6">
                    {years.map(year => {
                        const champ = getChamp(year);
                        const semis = getSemis(year);
                        return (
                            <div key={year} className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                                <div className="flex items-center justify-between mb-4">
                                    <span className="text-[var(--org-accent,#fb923c)] font-black text-xl">{year}</span>
                                    <span className="text-gray-500 text-xs">{champ?.location}</span>
                                </div>

                                {semis.length > 0 && (
                                    <div className="mb-4">
                                        <p className="text-gray-500 text-xs uppercase tracking-wide mb-2">Semifinals</p>
                                        {semis.map(s => (
                                            <GameRow key={s.id}
                                                label={`Semi ${s.semifinal}`}
                                                winner={s.winner}
                                                loser={s.loser}
                                                winnerScore={s.winner_score}
                                                loserScore={s.loser_score}
                                            />
                                        ))}
                                    </div>
                                )}

                                {champ && (
                                    <div>
                                        <p className="text-gray-500 text-xs uppercase tracking-wide mb-2">Championship</p>
                                        <div className="flex items-center gap-3">
                                            <span className="text-gray-500 text-xs w-24 text-right shrink-0">{TROPHY} Winner</span>
                                            <div className="flex-1 bg-[var(--org-accent,#f97316)]/10 border border-[var(--org-accent,#f97316)]/30 rounded-lg px-3 py-2 flex items-center justify-between">
                                                <div>
                                                    <p className="text-white font-black text-sm">{champ.champion}</p>
                                                    <p className="text-[var(--org-accent,#fb923c)] text-xs">Seed #{champ.champ_seed}</p>
                                                </div>
                                                <span className="text-white font-black text-lg">{champ.champ_score}</span>
                                            </div>
                                            <span className="text-gray-600 text-xs">vs</span>
                                            <div className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 flex items-center justify-between">
                                                <div>
                                                    <p className="text-gray-300 text-sm">{champ.runner_up}</p>
                                                    <p className="text-gray-500 text-xs">Seed #{champ.runnerup_seed}</p>
                                                </div>
                                                <span className="text-gray-400 font-black text-lg">{champ.runnerup_score}</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default History;